import {useContext, useState} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {
  atomButtonColors,
  atomButtonDesigns,
  atomButtonShapes,
  atomButtonSizes
} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import MoleculeInputField from '@s-ui/react-molecule-input-field'
import {useLocation, useRouter} from '@s-ui/react-router'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import {ROUTES} from '../../../../../config/index.js'
import {USER_DATA_FIELDS, useUtils} from '../../utils/useUtils.js'

const UserFormSection = ({userData}) => {
  const {domain} = useContext(Context)
  const {pathname, search} = useLocation()
  const router = useRouter()
  const [showGenericError, setShowGenericError] = useState(false)
  const [hasClientErrors, setHasClientErrors] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [currentUserData, setCurrentUserData] = useState({...userData})
  const {track} = useAnalyticsTrack()

  const isPurchaseFlow = pathname === ROUTES.USER.PROFILE_COMPLETE.PURCHASE

  const config = domain.get('config')
  const {UPDATE_PHONE_USER_ERROR} = config.get('ERRORS')
  const {TRACKING_EVENT_NAMES} = config.get('tracking')

  const {i18n} = useContext(Context)
  const {
    getUserDataWithErrors,
    getUserDataPojo,
    checkFieldErrors,
    purchaseFormFields,
    defaultFormFields,
    isPhoneOrZipCodeField
  } = useUtils()

  const userDataExists = Object.keys(userData).length > 0

  const formFields = isPurchaseFlow ? purchaseFormFields : defaultFormFields

  const handleOnChange = ({id, value}) => {
    const fieldHasErrors = checkFieldErrors(id, value)
    if (fieldHasErrors) {
      if (isPurchaseFlow && id === USER_DATA_FIELDS.ZIP_CODE) {
        domain
          .get('get_event_tracking_data_use_case')
          .execute({
            event: TRACKING_EVENT_NAMES.PERSONAL_DATA_FORM_ERROR,
            pageError: 'invalid_postcode'
          })
          .then(trackingData => {
            track(trackingData)
          })
      }

      setCurrentUserData({
        ...currentUserData,
        [id]: {value, error: true}
      })
      setHasClientErrors(true)
    } else {
      setCurrentUserData({...currentUserData, [id]: {value, error: false}})
      setHasClientErrors(false)
    }
  }

  const handlePurchaseOnClick = async () => {
    const parsedCurrentUserData = getUserDataPojo(currentUserData)

    const [error] = await domain.get('update_data_user_use_case').execute({
      useNewEndpoint: true,
      userData: parsedCurrentUserData
    })

    setIsLoading(false)
    if (error) {
      if (error instanceof UPDATE_PHONE_USER_ERROR) {
        domain
          .get('get_event_tracking_data_use_case')
          .execute({
            event: TRACKING_EVENT_NAMES.PERSONAL_DATA_FORM_ERROR,
            pageError: 'phone_number_exists'
          })
          .then(trackingData => {
            track(trackingData)
          })

        setCurrentUserData({
          ...currentUserData,
          [USER_DATA_FIELDS.PHONE]: {
            ...currentUserData[UPDATE_PHONE_USER_ERROR.PHONE],
            error: true,
            errorText: i18n.t('COMMON.PHONE_ALREADY_REGISTERED')
          }
        })
      } else {
        domain
          .get('get_event_tracking_data_use_case')
          .execute({
            event: TRACKING_EVENT_NAMES.PERSONAL_DATA_FORM_ERROR,
            pageError: 'generic_error'
          })
          .then(trackingData => {
            track(trackingData)
          })
        setShowGenericError(true)
      }
    } else {
      router.push({
        pathname: ROUTES.TRANSACTION.TERMS,
        search
      })
    }
  }

  const manageUserDataErrors = () => {
    const userDataWithErrors = getUserDataWithErrors({userData: currentUserData})
    setCurrentUserData(userDataWithErrors)

    const hasErrors = Object.keys(userDataWithErrors)
      .map(field => userDataWithErrors[field].error)
      .some(Boolean)

    setHasClientErrors(hasErrors)
    return hasErrors
  }

  const handleOnClick = () => {
    setShowGenericError(false)
    setIsLoading(true)

    if (manageUserDataErrors()) {
      return
    }
    if (isPurchaseFlow) {
      handlePurchaseOnClick()
    }
  }

  return (
    <div className="mt-UserProfileComplete-userFormSection">
      {userDataExists &&
        formFields.map(({errorText, id, label}) => (
          <MoleculeInputField
            disabled={id === USER_DATA_FIELDS.EMAIL}
            errorText={currentUserData[id].error ? i18n.t(currentUserData[id].errorText || errorText) : ''}
            id={id}
            key={id}
            label={i18n.t(label, 0)}
            onChange={e => handleOnChange({id, value: e.target.value})}
            tabIndex={0}
            type={isPhoneOrZipCodeField(id) ? 'number' : 'text'}
            value={currentUserData[id].value}
          />
        ))}
      {showGenericError && (
        <div className="mt-UserProfileComplete-userFormError">{i18n.t('CALL_ME_BACK_GENERIC_ERROR_TEXT')}</div>
      )}
      <div className="mt-UserProfileComplete-userFormButton">
        <AtomButton
          color={atomButtonColors.PRIMARY}
          design={atomButtonDesigns.SOLID}
          disabled={hasClientErrors}
          fullWidth
          isLoading={isLoading}
          onClick={handleOnClick}
          shape={atomButtonShapes.CIRCULAR}
          size={atomButtonSizes.LARGE}
        >
          {i18n.t('COMMON.CONTINUE')}
        </AtomButton>
      </div>
    </div>
  )
}

UserFormSection.displayName = 'UserFormSection'
UserFormSection.propTypes = {
  userData: PropTypes.object
}

export default UserFormSection
